import type { Functions } from 'firebase/functions'
import { httpsCallable } from 'firebase/functions'
import { extractDomainFromEmail } from './index'
import { inferMimeTypeFromUrl } from './mime-type'
import { FaviconStorageModule } from './storage'

export interface FaviconStorageItem {
    domain: string
    blob: string | null
    mimeType: string | null
    fetchedAt: number
}

export interface FaviconServiceDeps {
    storage: FaviconStorageModule
    functions: Functions
}

export interface FaviconFetchRequest {
    domain: string
}

export interface FaviconResult {
    domain: string
    faviconUrl: string | null
    faviconData: string | null
    status: 'success' | 'not-found' | 'error' | 'cors-error'
    fetchedAt: number
    mimeType: string | null
}

export class FaviconService {
    private readonly pendingFetches = new Map<string, Promise<FaviconResult>>()
    private readonly storage: FaviconStorageModule

    constructor(private readonly deps: FaviconServiceDeps) {
        this.storage = deps.storage
    }

    /**
     * Get favicon for a domain. Returns cached version if available from storage,
     * otherwise queues for fetching and returns a promise that resolves with the favicon data URL.
     */
    async getFaviconForDomain(domain: string): Promise<string | null> {
        const cached = await this.storage.getFavicon(domain)
        if (cached) {
            return this.faviconToDataUrl(cached)
        }

        let fetchPromise = this.pendingFetches.get(domain)
        if (!fetchPromise) {
            fetchPromise = this._fetchForDomain(domain).finally(() => {
                this.pendingFetches.delete(domain)
            })
            this.pendingFetches.set(domain, fetchPromise)
        }
        const result = await fetchPromise

        const item = await this.storage.getFavicon(domain)
        let dataUrl = item ? this.faviconToDataUrl(item) : null
        if (!dataUrl) {
            dataUrl = this.resultToDataUrl(result)
        }
        return dataUrl
    }

    /**
     * Get favicons for multiple domains (checks storage; queues fetches for missing)
     */
    async getFaviconsForDomains(
        domains: string[],
    ): Promise<Record<string, string | null>> {
        const result: Record<string, string | null> = {}

        for (const domain of domains) {
            const cachedItem = (await this.storage.getFavicons([domain]))[
                domain
            ]
            if (cachedItem) {
                result[domain] = this.faviconToDataUrl(cachedItem)
            } else {
                result[domain] = null
                this.getFaviconForDomain(domain).catch((error) => {
                    console.warn('[FaviconService] Prefetch failed', error)
                })
            }
        }

        return result
    }

    /**
     * Get favicon for an email address
     */
    async getFaviconForEmail(email: string): Promise<string | null> {
        const domain = extractDomainFromEmail(email)
        return this.getFaviconForDomain(domain)
    }

    /**
     * Force refresh a favicon (bypass caches)
     */
    async refreshFavicon(domain: string): Promise<string | null> {
        this.pendingFetches.delete(domain)

        const result = await this._fetchForDomain(domain)
        const item = await this.storage.getFavicon(domain)
        let dataUrl = item ? this.faviconToDataUrl(item) : null
        if (!dataUrl) {
            dataUrl = this.resultToDataUrl(result)
        }
        return dataUrl
    }

    /**
     * Fetch favicon using cloud function as fallback for CORS issues
     */
    private async fetchFaviconFromCloudFunction(
        domain: string,
    ): Promise<FaviconResult> {
        const fetchFaviconCloud = httpsCallable(
            this.deps.functions,
            'fetchFavicon',
        )

        const result = await fetchFaviconCloud({ domain })

        const data = result.data as {
            domain: string
            faviconUrl: string | null
            faviconData: string | null
            status: 'success' | 'not-found' | 'error' | 'cors-error'
            fetchedAt: number
            mimeType: string | null
        }

        return {
            domain: data.domain,
            faviconUrl: data.faviconUrl,
            faviconData: data.faviconData,
            status: data.status,
            fetchedAt: data.fetchedAt,
            mimeType: data.mimeType ?? null,
        }
    }

    private async _fetchForDomain(domain: string): Promise<FaviconResult> {
        return this.fetchFaviconFromCloudFunction(domain)
    }

    private faviconToDataUrl(item: FaviconStorageItem): string | null {
        if (item.blob) {
            const mimeType = item.mimeType ?? 'image/png'
            return `data:${mimeType};base64,${item.blob}`
        }
        return null
    }

    private resultToDataUrl(result: FaviconResult): string | null {
        if (!result.faviconData) {
            return null
        }
        const mimeType =
            result.mimeType ?? inferMimeTypeFromUrl(result.faviconUrl)
        return `data:${mimeType ?? 'image/png'};base64,${result.faviconData}`
    }

    async preloadFavicons(domains: string[]): Promise<void> {
        const uniqueDomains = [...new Set(domains)]
        await this.storage.getFavicons(uniqueDomains)
    }
}
